import React, { SyntheticEvent, useEffect, useState } from "react";
import styled from "styled-components";
import { Checkbox } from "semantic-ui-react";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import BootstrapTable from "react-bootstrap-table-next";
import { useHistory, useLocation, useParams } from "react-router";
import { CheckboxProps } from "semantic-ui-react/dist/commonjs/modules/Checkbox/Checkbox";
import Spinner from "react-bootstrap/Spinner";
import {
  DisconnectEntrataProperty,
  EntrataProperty,
  EntrataUser,
  LinkedProperties,
  LoadAvailableProperties,
  LoadEntrataUsers,
  SaveEntrataUserL1,
} from "../../../services/IntegrationApi";
import { useSnackbar } from "../../../hooks/useSnackbar";
import useAuth from "../../login/AuthContext";
import { ENTRATA_URL } from "./EntrataUsers";
import { EntrataUserL1MappingModal } from "./EntrataUserL1MappingModal";
import { TimedAlert } from "../../../components/TimedAlert";
import { UnitGroupingSelector } from "../../../components/UnitGroupingSelector";

import { normalize } from "../../../helpers/normalize";
import { MainLayout } from "../../../layout/MainLayout";
import { URL } from "../../../constants/navigation";
import { UNIT_GROUPING} from "../../../constants/types";
import {getReadbleDate} from "../../../helpers/getReadbleDate";
import compose from "ramda/src/compose";
import join from "ramda/src/join";
import { handleNavigation } from "../../../helpers/handleNavigation";

type EntrataUserContainerProps = {
  userIn: EntrataUser;
};

const TableWrapper = styled.div`
  padding: 10px;
`;

const Title = styled.div`
  margin: 16px 0px;
  font-weight: bold;
`;

const Centered = styled.div`
  display: flex;
  margin: 24px 0px;
  flex-direction: row;
`;

const Indicator = styled(Spinner)`
  margin-top: 64px;
`;

export const ENTRATA_USER_URL = `${URL.ENTRATA}/user/:userId`;
export const ENTRATA_USER_PROPERTY_URL = `${URL.ENTRATA}/user/:userId/property/:propertyId`;

const getDate = compose(getReadbleDate("YYYY/M/D"), join("/"));

export const EntrataUserContainer: React.FunctionComponent<EntrataUserContainerProps> = ({ userIn }) => {
  const history = useHistory();
  const { getAuthToken } = useAuth();
  const { userId } = useParams<{ userId: string }>();
  const { showErrorSnackbar } = useSnackbar();

  const location = useLocation<{ user: EntrataUser }>();

  const [user, setUser] = useState<EntrataUser | null | undefined>(userIn);
  const [error, setError] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [hasTriedLoading, setHasTriedLoading] = useState<boolean>(false);
  const [disconnectTarget, setDisconnectTarget] = useState<LinkedProperties | null>(null);

  useEffect(() => {
    history.replace(location.pathname, { user });
  }, [history, location.pathname, normalize(user)]);

  useEffect(() => {
    if (user == null && location.state?.user) {
      setUser(location.state.user);
    }
  }, [user, location.state]);

  // Load user data from API if not available from props or location state
  useEffect(() => {
    const loadUserFromApi = async () => {
      if (user == null && !location.state?.user && userId && !hasTriedLoading) {
        setLoading(true);
        setHasTriedLoading(true);
        try {
          let foundUser: EntrataUser | undefined;
          let page = 0;
          const maxPages = 10; // Prevent infinite loop, adjust as needed

          // Search through pages until we find the user or reach max pages
          while (!foundUser && page < maxPages) {
            const results = await LoadEntrataUsers(page, await getAuthToken());
            foundUser = results.content.find((u: EntrataUser) => u.id.toString() === userId);

            // If this page has fewer items than expected, we've reached the end
            if (results.content.length === 0) {
              break;
            }
            page++;
          }

          if (foundUser) {
            setUser(foundUser);
          }
          // Note: Removed history.push from here to avoid state update during render
        } catch (error) {
          showErrorSnackbar('Failed to load user data');
          // Note: Removed history.push from here to avoid st-ate update during render
        } finally {
          setLoading(false);
        }
      }
    };

    loadUserFromApi();
  }, [user, location.state, userId, getAuthToken, hasTriedLoading]);

  // Handle redirect when user is not found
  useEffect(() => {
    // Redirect if we don't have a user and we've finished trying to load one
    if (user == null && !location.state?.user && !loading && hasTriedLoading) {
      history.push(ENTRATA_URL);
    }
    // Also redirect if there's no userId and no location state
    else if (user == null && !location.state?.user && !userId && !loading) {
      history.push(ENTRATA_URL);
    }
  }, [user, location.state, loading, hasTriedLoading, userId, history]);

  if (loading) {
    return (
      <MainLayout>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '50px' }}>
          <Spinner animation="border" variant="secondary" />
        </div>
      </MainLayout>
    );
  }

  if (user == null) {
    // Show loading or empty state while waiting for redirect or data
    return <div />;
  }

  const handleDisconnect = async (row: LinkedProperties) => {
    try {
      await DisconnectEntrataProperty(user.id, row.l1Id, row.propertyId, await getAuthToken());
      const updatedUser = {
        ...user,
        properties: user.properties.filter((p) => p !== row),
      };
      setUser(updatedUser);
    } catch {
      setError(true);
    } finally {
      setDisconnectTarget(null);
    }
  };

  const columns = [
    {
      dataField: "propertyName",
      text: "Property Name",
    },
    {
      dataField: "l1Id",
      text: "l1Id",
    },
    {
      dataField: "propertyId",
      text: "Entrata Property Id",
    },
    {
      dataField: "enabled",
      text: "Import Enabled",
      formatter: (value: boolean, row: LinkedProperties) => (
        <Checkbox
          toggle
          checked={value}
          onChange={async (event: React.FormEvent<HTMLInputElement>, data: CheckboxProps) => {
            event.stopPropagation();

            if (user) {
              const index = user.properties.indexOf(row);
              const a = { ...user };
              a.properties.splice(index, 1, { ...row, enabled: !!data.checked });

              SaveEntrataUserL1(
                a.id,
                row.propertyId,
                {
                  enabled: !!data.checked,
                  importApplicants: row.importApplicants,
                  importSignedApplicants: row.importSignedApplicants,
                  signedApplicationEventType: row.signedApplicationEventType,
                  l1Id: row.l1Id,
                  unitGrouping: row.unitGrouping,
                  propertyName: row.propertyName,
                  importFullRooms: row.importFullRooms,
                  notifyFullRooms: row.notifyFullRooms,
                  importMonthToMonth: row.importMonthToMonth,
                  importAllCurrentLeases: row.importAllCurrentLeases,
                },
                await getAuthToken()
              ).then(
                () => setUser(a),
                () => setError(true)
              );
            }
          }}
        />
      ),
    },
    {
      dataField: "importApplicants",
      text: "Import Applicants",
      formatter: (value: boolean, row: LinkedProperties) => (
        <Checkbox
          toggle
          checked={value}
          onChange={async (event: React.FormEvent<HTMLInputElement>, data: CheckboxProps) => {
            event.stopPropagation();

            if (user) {
              const index = user.properties.indexOf(row);
              const a = { ...user };
              a.properties.splice(index, 1, {
                ...row,
                importApplicants: !!data.checked,
              });

              SaveEntrataUserL1(
                a.id,
                row.propertyId,
                {
                  enabled: row.enabled,
                  importApplicants: !!data.checked,
                  importSignedApplicants: row.importSignedApplicants,
                  signedApplicationEventType: row.signedApplicationEventType,
                  l1Id: row.l1Id,
                  unitGrouping: row.unitGrouping,
                  propertyName: row.propertyName,
                  importFullRooms: row.importFullRooms,
                  notifyFullRooms: row.notifyFullRooms,
                  importMonthToMonth: row.importMonthToMonth,
                  importAllCurrentLeases: row.importAllCurrentLeases,
                },
                await getAuthToken()
              ).then(
                () => setUser(a),
                () => setError(true)
              );
            }
          }}
        />
      ),
    },{
      dataField: "importSignedApplicants",
      text: "Import Signed Applicants",
      formatter: (value: boolean, row: LinkedProperties) => (
        <Checkbox
          toggle
          checked={value}
          onChange={async (event: React.FormEvent<HTMLInputElement>, data: CheckboxProps) => {
            event.stopPropagation();

            if (user) {
              const index = user.properties.indexOf(row);
              const a = { ...user };
              a.properties.splice(index, 1, {
                ...row,
                importSignedApplicants: !!data.checked,
              });

              SaveEntrataUserL1(
                a.id,
                row.propertyId,
                {
                  enabled: row.enabled,
                  importApplicants: row.importApplicants,
                  importSignedApplicants: !!data.checked,
                  signedApplicationEventType: row.signedApplicationEventType,
                  l1Id: row.l1Id,
                  unitGrouping: row.unitGrouping,
                  propertyName: row.propertyName,
                  importFullRooms: row.importFullRooms,
                  notifyFullRooms: row.notifyFullRooms,
                  importMonthToMonth: row.importMonthToMonth,
                  importAllCurrentLeases: row.importAllCurrentLeases,
                },
                await getAuthToken()
              ).then(
                () => setUser(a),
                () => setError(true)
              );
            }
          }}
        />
      ),
    },
    {
      dataField: "importFullRooms",
      text: "Import Complete Units",
      formatter: (value: boolean, row: LinkedProperties) => (
        <Checkbox
          toggle
          checked={value}
          onChange={async (event: React.FormEvent<HTMLInputElement>, data: CheckboxProps) => {
            event.stopPropagation();

            if (user) {
              const index = user.properties.indexOf(row);
              const a = { ...user };
              a.properties.splice(index, 1, {
                ...row,
                importFullRooms: !!data.checked,
              });
              SaveEntrataUserL1(
                a.id,
                row.propertyId,
                {
                  enabled: row.enabled,
                  importApplicants: row.importApplicants,
                  importSignedApplicants: row.importSignedApplicants,
                  signedApplicationEventType: row.signedApplicationEventType,
                  l1Id: row.l1Id,
                  unitGrouping: row.unitGrouping,
                  propertyName: row.propertyName,
                  importFullRooms: !!data.checked,
                  notifyFullRooms: row.notifyFullRooms,
                  importMonthToMonth: row.importMonthToMonth,
                  importAllCurrentLeases: row.importAllCurrentLeases,
                },
                await getAuthToken()
              ).then(
                () => setUser(a),
                () => setError(true)
              );
            }
          }}
        />
      ),
    },
      {
      dataField: "notifyFullRooms",
      text: "Notify Complete Units",
      formatter: (value: boolean, row: LinkedProperties) => (
        <Checkbox
          toggle
          checked={value}
          onChange={async (event: React.FormEvent<HTMLInputElement>, data: CheckboxProps) => {
            event.stopPropagation();

            if (user) {
              const index = user.properties.indexOf(row);
              const a = { ...user };
              a.properties.splice(index, 1, {
                ...row,
                notifyFullRooms: !!data.checked,
              });
              SaveEntrataUserL1(
                a.id,
                row.propertyId,
                {
                  enabled: row.enabled,
                  importApplicants: row.importApplicants,
                  importSignedApplicants: row.importSignedApplicants,
                  signedApplicationEventType: row.signedApplicationEventType,
                  l1Id: row.l1Id,
                  unitGrouping: row.unitGrouping,
                  propertyName: row.propertyName,
                  notifyFullRooms: !!data.checked,
                  importFullRooms: row.importFullRooms,
                  importMonthToMonth: row.importMonthToMonth,
                  importAllCurrentLeases: row.importAllCurrentLeases,
                },
                await getAuthToken()
              ).then(
                () => setUser(a),
                () => setError(true)
              );
            }
          }}
        />
      ),
    },
    {
      dataField: "regEx",
      text: "Unit Id Regex",
      formatter: (value: string, row: LinkedProperties) => (
        <>
          {row.unitGrouping === UNIT_GROUPING.UNIT_ID && <del>{value}</del>}
          {row.unitGrouping !== UNIT_GROUPING.UNIT_ID && value}
        </>
      ),
    },
    {
      dataField: "unitGrouping",
      text: "Unit Grouping",
      formatter: (value: UNIT_GROUPING, row: LinkedProperties) => (
        <UnitGroupingSelector
          row={row}
          userId={user.id}
          defaultValue={value}
          handleError={setError}
          onChange={(unitGrouping: UNIT_GROUPING) => {
            if (!user) return;

            const index = user.properties.indexOf(row);
            const a = { ...user };
            a.properties.splice(index, 1, {
              ...row,
              unitGrouping,
            });

            setUser(a);
          }}
        />
      ),
    },
    {
      dataField: "importMonthToMonth",
      text: "Import Month to Month",
      formatter: (value: boolean, row: LinkedProperties) => (
        <Checkbox
          toggle
          checked={value}
          onChange={async (event: React.FormEvent<HTMLInputElement>, data: CheckboxProps) => {
            event.stopPropagation();

            if (user) {
              const index = user.properties.indexOf(row);
              const a = { ...user };
              a.properties.splice(index, 1, {
                ...row,
                importMonthToMonth: !!data.checked,
              });
              SaveEntrataUserL1(
                a.id,
                row.propertyId,
                {
                  enabled: row.enabled,
                  importApplicants: row.importApplicants,
                  importSignedApplicants: row.importSignedApplicants,
                  signedApplicationEventType: row.signedApplicationEventType,
                  l1Id: row.l1Id,
                  unitGrouping: row.unitGrouping,
                  propertyName: row.propertyName,
                  importFullRooms: row.importFullRooms,
                  notifyFullRooms: row.notifyFullRooms,
                  importMonthToMonth: !!data.checked,
                  importAllCurrentLeases: row.importAllCurrentLeases,
                },
                await getAuthToken()
              ).then(
                () => setUser(a),
                () => setError(true)
              );
            }
          }}
        />
      ),
    },
    {
      dataField: "importAllCurrentLeases",
      text: "Import All Current Leases",
      formatter: (value: boolean, row: LinkedProperties) => (
        <Checkbox
          toggle
          checked={value}
          onChange={async (event: React.FormEvent<HTMLInputElement>, data: CheckboxProps) => {
            event.stopPropagation();

            if (user) {
              const index = user.properties.indexOf(row);
              const a = { ...user };
              a.properties.splice(index, 1, {
                ...row,
                importAllCurrentLeases: !!data.checked,
              });
              SaveEntrataUserL1(
                a.id,
                row.propertyId,
                {
                  enabled: row.enabled,
                  importApplicants: row.importApplicants,
                  importSignedApplicants: row.importSignedApplicants,
                  signedApplicationEventType: row.signedApplicationEventType,
                  l1Id: row.l1Id,
                  unitGrouping: row.unitGrouping,
                  propertyName: row.propertyName,
                  importFullRooms: row.importFullRooms,
                  notifyFullRooms: row.notifyFullRooms,
                  importMonthToMonth: row.importMonthToMonth,
                  importAllCurrentLeases: !!data.checked,
                },
                await getAuthToken()
              ).then(
                () => setUser(a),
                () => setError(true)
              );
            }
          }}
        />
      ),
    },
    {
      dataField: "lastRun",
      text: "Last Run Time",
      formatter: (value: [number, number, number, number, number, number], row: LinkedProperties) => {
          return getDate(value);
      },
    },
    {
      dataField: "actions",
      text: "Options",
      isDummyField: true,
      formatter: (_value: any, row: LinkedProperties) => (
        <img
          src="/static/images/icons/disconnect.png"
          alt="Disconnect"
          title="Disconnect"
          style={{ width: 24, height: 24, cursor: "pointer" }}
          onClick={(e) => {
            e.stopPropagation();
            setDisconnectTarget(row);
          }}
        />
      ),
    },
  ];

  const existingPropertyEventHandler = {
    onClick: (e: SyntheticEvent, row: LinkedProperties) => {
      handleNavigation(e as any, `${user.id}/property/${row.propertyId}/mappings`, history.push, {
        user,
        property: row,
        l1Id: row.l1Id,
      });
    },
  };

  return (
    <MainLayout>
      <Centered>
        <Button variant="secondary" onClick={history.goBack}>
          Go Back
        </Button>
      </Centered>

      <TimedAlert variant="danger" visible={error} setVisible={setError}>
        Unable to update
      </TimedAlert>

      <Modal show={!!disconnectTarget} onHide={() => setDisconnectTarget(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Disconnect</Modal.Title>
        </Modal.Header>
        <Modal.Body>Are you sure you want to disconnect this network?</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDisconnectTarget(null)}>
            No
          </Button>
          <Button variant="danger" onClick={() => disconnectTarget && handleDisconnect(disconnectTarget)}>
            Yes
          </Button>
        </Modal.Footer>
      </Modal>

      <TableWrapper>
        <Title>Existing Properties</Title>
        <BootstrapTable
          hover
          keyField="l1Id"
          bordered={false}
          columns={columns}
          rowClasses="entrata-row"
          headerClasses="entrata-header"
          rowEvents={existingPropertyEventHandler}
          data={user.properties}
        />
      </TableWrapper>

      <TableWrapper>
        <Title>Available Properties</Title>
        <AvailableEntrataProperties user={user} existingProperties={user.properties.map((x) => x.propertyId)} />
      </TableWrapper>
    </MainLayout>
  );
};

const AvailableEntrataProperties: React.FunctionComponent<{
  user: EntrataUser;
  existingProperties: number[];
}> = ({ user, existingProperties }) => {
  const { getAuthToken } = useAuth();
  const { showErrorSnackbar } = useSnackbar();

  const [loading, setLoading] = useState<boolean>(true);
  const [properties, setProperties] = useState<EntrataProperty[]>([]);
  const [modalState, setModalState] = useState<{
    show: boolean;
    property: EntrataProperty;
  }>({ show: false, property: { propertyId: 0, propertyName: "" } });

  useEffect(() => {
    async function load() {
      const entrataProperties = await LoadAvailableProperties(user.id, await getAuthToken());
      if (entrataProperties.error) {
        showErrorSnackbar(entrataProperties.error.description);
        return;
      }

      if (!entrataProperties?.length) {
        return;
      }

      setProperties(entrataProperties.filter((x: EntrataProperty) => existingProperties.indexOf(x.propertyId) === -1));
      setLoading(false);
    }

    load();
  }, []);

  const newPropertyEventHandler = {
    onClick: (e: SyntheticEvent, row: EntrataProperty, rowIndex: number) => {
      setModalState({ ...modalState, show: true, property: row });
    },
  };

  const column = [
    {
      dataField: "propertyName",
      text: "Property Name",
    },
    {
      dataField: "propertyId",
      text: "Property Id",
    },
  ];

  return (
    <div>
      <EntrataUserL1MappingModal
        show={modalState.show}
        property={modalState.property}
        closeModal={() => {
          setModalState({ ...modalState, show: false });
        }}
        entrataUserId={user.id}
      />
      <BootstrapTable
        hover
        bordered={false}
        columns={column}
        data={properties}
        keyField="propertyId"
        rowClasses="entrata-row"
        headerClasses="entrata-header"
        rowEvents={newPropertyEventHandler}
      />
      {loading && <Indicator animation="border" variant="secondary" />}
    </div>
  );
};
